import { useState } from 'react';
import { streamingPlans, streamFormats, streamStatusOptions } from '@/mocks/streamingPlans';

type StreamingMode = 'own-streaming' | 'rfw-hosting';

export default function StreamingHostingSection() {
  const [selectedMode, setSelectedMode] = useState<StreamingMode>('own-streaming');
  const [streamUrl, setStreamUrl] = useState('');
  const [format, setFormat] = useState('');
  const [status, setStatus] = useState('pending');
  const [testing, setTesting] = useState(false);
  const [tested, setTested] = useState(false);

  const activePlan = streamingPlans.find((p) => p.id === selectedMode)!;
  const statusOption = streamStatusOptions.find((s) => s.value === status)!;

  const handleTestSignal = () => {
    if (!streamUrl.trim() || testing) return;
    setTesting(true);
    setTested(false);
    setTimeout(() => {
      setTesting(false);
      setTested(true);
      setStatus('online');
    }, 2500);
  };

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full max-w-5xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
            <i className="ri-broadcast-line text-xs"></i>
            Streaming Hosting
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
            ¿Cómo transmitís tu radio?
          </h2>
          <p className="mt-3 text-foreground-500 text-sm max-w-xl mx-auto">
            Elegí entre conectar tu streaming actual o contratar hosting de streaming administrado por RFW.
          </p>
        </div>

        {/* Two options toggle */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {streamingPlans.map((plan) => {
            const isActive = selectedMode === plan.id;
            const isHighlighted = plan.highlighted;

            return (
              <button
                key={plan.id}
                onClick={() => {
                  setSelectedMode(plan.id as StreamingMode);
                  setTested(false);
                  setStatus('pending');
                }}
                className={`relative rounded-2xl p-5 text-left cursor-pointer transition-all duration-200 border-2 ${
                  isActive
                    ? isHighlighted
                      ? 'border-accent-500 bg-accent-50/30 ring-2 ring-accent-200'
                      : 'border-primary-500 bg-primary-50/30 ring-2 ring-primary-200'
                    : 'border-background-200 bg-background-50 hover:border-background-300 hover:shadow-sm'
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <span
                    className={`absolute -top-2.5 right-4 px-3 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${
                      isHighlighted
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-secondary-500 text-background-50'
                    }`}
                  >
                    {plan.comingSoon ? 'Próximamente' : plan.badge}
                  </span>
                )}

                <div className="flex items-start gap-3">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                      isActive
                        ? isHighlighted
                          ? 'bg-accent-100'
                          : 'bg-primary-100'
                        : 'bg-background-100'
                    }`}
                  >
                    <i
                      className={`text-lg ${
                        isActive
                          ? isHighlighted
                            ? 'ri-cloud-line text-accent-600'
                            : 'ri-link text-primary-600'
                          : 'text-foreground-400'
                      } ${isHighlighted ? 'ri-cloud-line' : 'ri-link'}`}
                    ></i>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-foreground-800">{plan.name}</p>
                    </div>
                    <p className="text-xs text-foreground-500 mt-0.5">{plan.tagline}</p>
                    <div className="flex items-baseline gap-1 mt-2">
                      <span className={`text-lg font-extrabold ${plan.comingSoon ? 'text-foreground-400' : 'text-foreground-950'}`}>
                        {plan.price}
                      </span>
                    </div>
                    <p className="text-[10px] text-foreground-400 mt-0.5">{plan.priceLabel}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Active content based on selection */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-7 animate-fade-in-up">
          {/* Ya tengo Streaming */}
          {selectedMode === 'own-streaming' && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 pb-4 border-b border-background-200">
                <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                  <i className="ri-link text-primary-600 text-sm"></i>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground-800">{activePlan.name}</h3>
                  <p className="text-[11px] text-foreground-400">{activePlan.description}</p>
                </div>
              </div>

              {/* Stream URL */}
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                  Stream URL
                </label>
                <div className="relative">
                  <input
                    type="url"
                    value={streamUrl}
                    onChange={(e) => {
                      setStreamUrl(e.target.value);
                      setTested(false);
                      setStatus('pending');
                    }}
                    placeholder="https://stream.turadio.com:8000/live"
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-300 font-mono outline-none focus:border-foreground-300 transition-colors"
                  />
                  {streamUrl && (
                    <button
                      onClick={() => {
                        setStreamUrl('');
                        setTested(false);
                        setStatus('pending');
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                    >
                      <i className="ri-close-line text-foreground-400 text-xs"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Format */}
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                  Formato
                </label>
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all appearance-none"
                >
                  <option value="">Seleccionar formato</option>
                  {streamFormats.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>

              {/* Estado */}
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                  Estado
                </label>
                <div className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200">
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${statusOption.dot} ${tested ? 'animate-pulse' : ''}`}></div>
                  <span className={`text-sm font-semibold ${statusOption.text}`}>{statusOption.label}</span>
                  {tested && status === 'online' && (
                    <span className="ml-auto text-[11px] text-emerald-600 font-medium">Señal activa</span>
                  )}
                </div>
              </div>

              {/* Probar señal button */}
              <button
                onClick={handleTestSignal}
                disabled={!streamUrl.trim() || testing}
                className={`w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  testing
                    ? 'bg-foreground-200 text-foreground-400 cursor-wait'
                    : !streamUrl.trim()
                      ? 'bg-foreground-100 text-foreground-300 cursor-not-allowed'
                      : 'bg-foreground-950 text-background-50 hover:bg-foreground-800 active:scale-[0.98]'
                }`}
              >
                {testing ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    <span>Probando señal...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-radar-line text-base"></i>
                    <span>Probar señal</span>
                  </>
                )}
              </button>

              {/* Benefits */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2">
                {activePlan.benefits.map((b) => (
                  <div key={b.label} className="flex items-start gap-2.5 p-3 rounded-xl bg-background-100/70">
                    <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className={`${b.icon} text-primary-600 text-xs`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground-800 mb-0.5">{b.label}</p>
                      <p className="text-[10px] text-foreground-500 leading-relaxed">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quiero Streaming con RFW */}
          {selectedMode === 'rfw-hosting' && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 pb-4 border-b border-background-200">
                <div className="w-9 h-9 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                  <i className="ri-cloud-line text-accent-600 text-sm"></i>
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-bold text-foreground-800">{activePlan.name}</h3>
                    <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-bold">
                      Próximamente
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground-400">{activePlan.description}</p>
                </div>
              </div>

              {/* Benefits grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {activePlan.benefits.map((b) => (
                  <div
                    key={b.label}
                    className="flex items-start gap-3 p-4 rounded-xl bg-accent-50/50 border border-accent-200"
                  >
                    <div className="w-9 h-9 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                      <i className={`${b.icon} text-accent-600 text-sm`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground-800 mb-0.5">{b.label}</p>
                      <p className="text-[10px] text-foreground-500 leading-relaxed">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Coming soon notice */}
              <div className="bg-accent-50 border border-accent-200 rounded-xl p-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <i className="ri-timer-line text-accent-600 text-sm"></i>
                  <span className="text-sm font-bold text-accent-700">Streaming con RFW — Próximamente disponible</span>
                </div>
                <p className="text-xs text-accent-600/70">
                  Estamos preparando la infraestructura para ofrecerte hosting de streaming 100% integrado. Mientras tanto, podés conectar tu streaming actual.
                </p>
              </div>

              {/* CTA — interest */}
              <button
                disabled
                className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold bg-foreground-100 text-foreground-300 cursor-not-allowed whitespace-nowrap"
              >
                <i className="ri-notification-3-line text-base"></i>
                <span>Disponible pronto</span>
              </button>
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <p className="text-center mt-6 text-[11px] text-foreground-400">
          <i className="ri-information-line mr-1"></i>
          Los precios de RFW Hosting están sujetos a cambio. Se anunciarán cuando el servicio esté disponible.
        </p>
      </div>
    </section>
  );
}